// MA-690 (slice ms-math-05-family-pictures) — the three part-whole pictures
// the ms.math.05 / ms.math.06 "which part is missing?" items are authored
// against, and that their prompts name ("split frame", "set picture",
// "part strip"). Before this slice all three collapsed onto one numeral
// number bond (docs/build/specs/MA-690-family-pictures.md).
//
// Registered in src/components/renderers/rendererRegistry.tsx as
// `split_ten_frame`, `set_picture` and `part_strip`. The mapper
// (contentToEngineItem.ts, MA-690 part-whole branch) sends
// `{ a: known part, b: 0, total: whole }`.
//
// QUESTION STATE ONLY (src/components/visuals/CONTRACT.md): every component
// here reads `a` (the known part) and `total` (the whole) and nothing else.
// It never reads `b`, and it never subtracts `a` from `total`: the missing
// part is ONE blue "?" element whose size does not depend on it. The only
// text nodes are the whole's numeral and "?". The aria-label states what a
// sighted child sees (the known count and the whole), never the missing
// count. Pinned by the whole-independence invariant in
// ma690FamilyPictures.test.mjs: for every kind and known part, the markup is
// byte-identical across all wholes except the whole's numeral (text node and
// aria), so the picture depends on the known part only, and its mark count
// grows by one counter's marks per known unit (so it draws nothing sized by
// the complement to ten either).
//
// Colours repeat the NB hint's bond (ScaffoldNB / NumberBondRenderer): the
// whole is gold, the known part terracotta, the missing part blue. So the NB
// hint rung, which stays on the ladder for these items, reads as the same
// two parts drawn as numerals: the abstract step after the picture.
import type { ReactElement } from "react";
import type { MathRendererProps } from "../renderers/rendererRegistry";

/** The largest whole these pictures draw (a ten frame's capacity). The
 *  mapper refuses a larger whole before it gets here; the components refuse
 *  it again (fail-safe, never a clipped or wrong picture). */
export const PART_WHOLE_PICTURE_MAX_WHOLE = 10;

/** Family marker each picture puts on its <svg> (`data-part-whole`). */
export const PART_WHOLE_FAMILY_ATTR = {
  split_ten_frame: "split-frame",
  set_picture: "two-sets",
  part_strip: "part-strip",
} as const;

const INK = "var(--ink, #3d3228)";
const GOLD = "var(--gold, #e8b84b)";
const TERRACOTTA = "var(--terracotta, #c96b4a)";
const HIDDEN_FILL = "var(--pal-blue, #5b8fc9)";
const CELL_LINE = "var(--color-border-secondary, #cdbfae)";
const PAPER = "var(--cream, #FAF3E8)";
/** Every picture uses the same viewBox width, content centred, so a small
 *  picture (one bead, four counters) is never scaled up to fill the column. */
const VB_W = 360;

/** The two numbers a picture may draw, or null when the payload is not a
 *  drawable part-whole (then the picture renders empty — never a guess). */
function partWholeInputs(a: unknown, total: unknown): { known: number; whole: number } | null {
  const known = Number(a);
  const whole = Number(total);
  if (!Number.isInteger(known) || !Number.isInteger(whole)) return null;
  if (!(known > 0 && known < whole && whole <= PART_WHOLE_PICTURE_MAX_WHOLE)) return null;
  return { known, whole };
}

function EmptyPicture({ label }: { label: string }): ReactElement {
  return <svg viewBox="0 0 10 10" width="0" height="0" role="img" aria-label={label} />;
}

/** The whole: a gold numeral badge above the picture, joined by a bracket
 *  that spans the whole picture (both parts together are the whole). */
function WholeBracket({ whole, x0, x1, top }: { whole: number; x0: number; x1: number; top: number }): ReactElement {
  const cx = (x0 + x1) / 2;
  const barY = top - 10;
  return (
    <g data-part="whole">
      <path d={`M${x0} ${top - 2} V${barY} H${x1} V${top - 2}`} fill="none" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <line x1={cx} y1={barY} x2={cx} y2={barY - 8} stroke={INK} strokeWidth="2" />
      <circle cx={cx} cy={barY - 28} r="20" fill={GOLD} stroke={INK} strokeWidth="1.5" />
      <text x={cx} y={barY - 22} textAnchor="middle" fontSize="18" fontFamily="Nunito, inherit" fontWeight={800}>
        {whole}
      </text>
    </g>
  );
}

function QuestionMark({ x, y, size = 30 }: { x: number; y: number; size?: number }): ReactElement {
  return (
    <text x={x} y={y + size * 0.35} textAnchor="middle" fontSize={size} fontFamily="Nunito, inherit" fontWeight={800} fill="white">
      ?
    </text>
  );
}

// ── 1. Split frame ─────────────────────────────────────────────────────────
// A frame split in two: the left piece is the `known` counters in square
// cells, rows of five as in a ten frame; the right piece is a closed lid with
// a "?" of FIXED size. Deliberately NOT a fixed-capacity ten frame: a frame
// of ten (or of `whole`) cells with the rest covered or left empty shows its
// complement (ten minus the known part, or the missing part itself), the
// countable-empties leak the MA-C10 / MA-687 rule forbids. So only the known
// part has cells, and a shorter last row is centred, never aligned to the row
// above, so no blank cell positions read as slots.
const SF_CELL = 46;
const SF_PITCH = 50;
const SF_TOP = 72;
const SF_LID_W = SF_PITCH * 1.5;
const SF_GAP = 10;

export function SplitTenFrameRenderer({ a, total }: MathRendererProps): ReactElement {
  const inputs = partWholeInputs(a, total);
  if (!inputs) return <EmptyPicture label="Split frame" />;
  const { known, whole } = inputs;
  const cols = Math.min(5, known);
  const rows = Math.ceil(known / 5);
  const blockW = cols * SF_PITCH - (SF_PITCH - SF_CELL);
  const blockH = rows * SF_PITCH - (SF_PITCH - SF_CELL);
  const pad = 6;
  const frameW = pad + blockW + SF_GAP + SF_LID_W + pad;
  const frameX = (VB_W - frameW) / 2;
  const blockX = frameX + pad;
  const blockY = SF_TOP + pad;
  const lidX = blockX + blockW + SF_GAP;
  const right = frameX + frameW;
  const H = SF_TOP + blockH + 2 * pad + 8;
  const cells: [number, number][] = [];
  for (let r = 0; r < rows; r++) {
    const inRow = Math.min(5, known - r * 5);
    const rowX = blockX + ((cols - inRow) * SF_PITCH) / 2;
    for (let c = 0; c < inRow; c++) cells.push([rowX + c * SF_PITCH, blockY + r * SF_PITCH]);
  }
  return (
    <svg viewBox={`0 0 ${VB_W} ${H}`} width="100%" style={{ maxWidth: VB_W }} role="img"
      data-part-whole={PART_WHOLE_FAMILY_ATTR.split_ten_frame}
      aria-label={`Split frame. ${known} ${known === 1 ? "counter is" : "counters are"} showing. The other part is under a lid. ${whole} in all.`}>
      <WholeBracket whole={whole} x0={frameX} x1={right} top={SF_TOP} />
      <rect data-part="frame" x={frameX} y={SF_TOP} width={frameW} height={blockH + 2 * pad} rx="10" fill={PAPER} stroke={INK} strokeWidth="2" />
      <g data-part="known">
        {cells.map(([x, y], i) => (
          <g key={i}>
            <rect x={x} y={y} width={SF_CELL} height={SF_CELL} rx="7" fill="none" stroke={CELL_LINE} strokeWidth="2" />
            <circle data-counter="known" cx={x + SF_CELL / 2} cy={y + SF_CELL / 2} r="16" fill={TERRACOTTA} stroke={INK} strokeWidth="1" />
          </g>
        ))}
      </g>
      <line x1={lidX - SF_GAP / 2} y1={SF_TOP - 4} x2={lidX - SF_GAP / 2} y2={SF_TOP + blockH + 2 * pad + 4} stroke={INK} strokeWidth="4" strokeLinecap="round" />
      <g data-part="missing">
        <rect x={lidX} y={blockY} width={SF_LID_W} height={blockH} rx="8" fill={HIDDEN_FILL} stroke={INK} strokeWidth="2" />
        <QuestionMark x={lidX + SF_LID_W / 2} y={blockY + blockH / 2} />
      </g>
    </svg>
  );
}

// ── 2. Set picture (two sets) ─────────────────────────────────────────────
// Two closed sets side by side. The left loop holds the `known` counters in
// rows of five (a shorter last row centred); the right loop is a fixed-size
// blue bag with a "?" and no counters, the same size for every item.
const SP_DOT_PITCH = 30;
const SP_DOT_R = 11;
const SP_TOP = 72;
const SP_BAG_W = 110;
const SP_GAP = 22;
const SP_LOOP_H = 104;

export function SetPictureRenderer({ a, total }: MathRendererProps): ReactElement {
  const inputs = partWholeInputs(a, total);
  if (!inputs) return <EmptyPicture label="Two sets" />;
  const { known, whole } = inputs;
  const cols = Math.min(5, known);
  const rows = Math.ceil(known / 5);
  const leftW = Math.max(SP_BAG_W, cols * SP_DOT_PITCH + 34);
  const leftX = (VB_W - (leftW + SP_GAP + SP_BAG_W)) / 2;
  const bagX = leftX + leftW + SP_GAP;
  const right = bagX + SP_BAG_W;
  const H = SP_TOP + SP_LOOP_H + 8;
  const dotsX0 = leftX + (leftW - (cols - 1) * SP_DOT_PITCH) / 2;
  const dotsY0 = SP_TOP + SP_LOOP_H / 2 - ((rows - 1) * SP_DOT_PITCH) / 2;
  return (
    <svg viewBox={`0 0 ${VB_W} ${H}`} width="100%" style={{ maxWidth: VB_W }} role="img"
      data-part-whole={PART_WHOLE_FAMILY_ATTR.set_picture}
      aria-label={`Two sets. One set has ${known} ${known === 1 ? "counter" : "counters"}. The other set is hidden. ${whole} in all.`}>
      <WholeBracket whole={whole} x0={leftX} x1={right} top={SP_TOP} />
      <g data-part="known">
        <rect x={leftX} y={SP_TOP} width={leftW} height={SP_LOOP_H} rx="44" fill={PAPER} stroke={INK} strokeWidth="2" />
        {Array.from({ length: known }, (_, i) => {
          // A shorter last row is centred, never left-aligned under the row
          // above, so its blank positions do not read as empty slots.
          const row = Math.floor(i / 5);
          const inRow = Math.min(5, known - row * 5);
          const rowX0 = dotsX0 + ((cols - inRow) * SP_DOT_PITCH) / 2;
          return (
            <circle key={i} data-counter="known" cx={rowX0 + (i % 5) * SP_DOT_PITCH} cy={dotsY0 + row * SP_DOT_PITCH}
              r={SP_DOT_R} fill={TERRACOTTA} stroke={INK} strokeWidth="1" />
          );
        })}
      </g>
      <g data-part="missing">
        <rect x={bagX} y={SP_TOP} width={SP_BAG_W} height={SP_LOOP_H} rx="44" fill={HIDDEN_FILL} stroke={INK} strokeWidth="2" />
        <QuestionMark x={bagX + SP_BAG_W / 2} y={SP_TOP + SP_LOOP_H / 2} size={36} />
      </g>
    </svg>
  );
}

// ── 3. Part strip ─────────────────────────────────────────────────────────
// One horizontal bar holding the `known` beads; the bar's far end runs under
// a blue cover card with a "?" (the split frame's lid metaphor). The card is
// taller than the bar, shares no outline with it and has ONE fixed size for
// every item, so the hidden part has no length that could be read as a
// count (MA-690 RED round 1, SHOULD-2: a bar SEGMENT of fixed width read as
// "about 3 beads" on a length model).
const PS_PITCH = 26;
const PS_TOP = 84;
const PS_H = 36;
const PS_TUCK = 24; // bar length that runs under the card (not visible)
const PS_CARD_W = 86;
const PS_CARD_OVER = 12; // the card overhangs the bar top and bottom

export function PartStripRenderer({ a, total }: MathRendererProps): ReactElement {
  const inputs = partWholeInputs(a, total);
  if (!inputs) return <EmptyPicture label="Part strip" />;
  const { known, whole } = inputs;
  const knownW = known * PS_PITCH + 10;
  const cardOffset = knownW - 4; // the card starts just past the last bead
  const x0 = (VB_W - (cardOffset + PS_CARD_W)) / 2;
  const cardX = x0 + cardOffset;
  const right = cardX + PS_CARD_W;
  const cardY = PS_TOP - PS_CARD_OVER;
  const cardH = PS_H + 2 * PS_CARD_OVER;
  const H = cardY + cardH + 8;
  const cy = PS_TOP + PS_H / 2;
  return (
    <svg viewBox={`0 0 ${VB_W} ${H}`} width="100%" style={{ maxWidth: VB_W }} role="img"
      data-part-whole={PART_WHOLE_FAMILY_ATTR.part_strip}
      aria-label={`Part strip. One part has ${known} ${known === 1 ? "bead" : "beads"}. The other part is under a cover. The whole is ${whole}.`}>
      <WholeBracket whole={whole} x0={x0} x1={right} top={cardY} />
      <g data-part="known">
        <rect x={x0} y={PS_TOP} width={knownW + PS_TUCK} height={PS_H} rx="10" fill={PAPER} stroke={INK} strokeWidth="2" />
        <line x1={x0 + 6} y1={cy} x2={x0 + knownW + PS_TUCK - 6} y2={cy} stroke={CELL_LINE} strokeWidth="3" />
        {Array.from({ length: known }, (_, i) => (
          <circle key={i} data-counter="known" cx={x0 + 5 + PS_PITCH / 2 + i * PS_PITCH} cy={cy} r="10" fill={TERRACOTTA} stroke={INK} strokeWidth="1" />
        ))}
      </g>
      <g data-part="missing">
        <rect x={cardX} y={cardY} width={PS_CARD_W} height={cardH} rx="12" fill={HIDDEN_FILL} stroke={INK} strokeWidth="2" />
        <QuestionMark x={cardX + PS_CARD_W / 2} y={cardY + cardH / 2} />
      </g>
    </svg>
  );
}
